import { SHIPPING_OPTION_FREE } from '../stores/shippingStore/shipping.consts'
import { logger } from './logger'
import { translateManager as t } from './TranslateManager'

const TWO_WEEKS_IN_MS = 1000 * 60 * 60 * 24 * 14

type HolidayId =
  | 'valentines_day'
  | 'mothers_day_uk'
  | 'mothers_day'
  | 'fathers_day'
  | 'veterans_day'
  | 'christmas'

export interface Holiday {
  id: HolidayId
  arrivesBeforeHolidayTextKey: string
  cartLineTextKey: string
  holidayDate: Date
  countries?: string[]
}

interface ShippingOptionWithArrival {
  shippingOptionName: string
  promisedArrivalDate: {
    iso: string
  }
}

interface GetEncourageExpressShippingParams {
  shippingOption: ShippingOptionWithArrival
  freeShippingOption: ShippingOptionWithArrival | undefined
  country: string | undefined
}

export function getEncourageExpressShippingForHolidayText({
  shippingOption,
  freeShippingOption,
  country,
}: GetEncourageExpressShippingParams): string | null {
  const holiday = getNextHoliday(country)
  if (!holiday) {
    return null
  }
  return shouldEncourageExpressShippingForHoliday({
    shippingOption,
    freeShippingOption,
    holidayDate: holiday.holidayDate,
  })
    ? t.get(holiday.arrivesBeforeHolidayTextKey)
    : null
}

interface GetNextHolidayReachableByShippingParams {
  shippingOptions: ShippingOptionWithArrival[]
  country: string | undefined
}

export function getNextHolidayReachableByShipping({
  shippingOptions,
  country,
}: GetNextHolidayReachableByShippingParams): Holiday | undefined {
  const holiday = getNextHoliday(country)
  if (!holiday) {
    return undefined
  }
  const freeShippingOption = shippingOptions.find(
    (option) => option.shippingOptionName === SHIPPING_OPTION_FREE
  )
  const hasOptionArrivingBeforeHoliday = shippingOptions.some(
    (shippingOption) =>
      shouldEncourageExpressShippingForHoliday({
        shippingOption,
        freeShippingOption,
        holidayDate: holiday.holidayDate,
      })
  )
  return hasOptionArrivingBeforeHoliday ? holiday : undefined
}

export function getNextHoliday(
  country: string | undefined
): Holiday | undefined {
  const holidayDetails = getHolidayDetails()
  const today = new Date()
  today.setHours(0, 0, 0, 0) // At midnight so comparison is easier

  return holidayDetails.find(
    (holiday) =>
      today <= holiday.holidayDate &&
      holiday.holidayDate.getTime() - today.getTime() < TWO_WEEKS_IN_MS &&
      isHolidayObservedIn(holiday, country)
  )
}

interface HasHolidayBetweenArrivalsParams {
  earlierArrivalIso: string
  laterArrivalIso: string
  country: string | undefined
}

export function hasHolidayBetweenArrivals({
  earlierArrivalIso,
  laterArrivalIso,
  country,
}: HasHolidayBetweenArrivalsParams): boolean {
  const earlierArrival = new Date(earlierArrivalIso)
  earlierArrival.setHours(0, 0, 0, 0)
  const laterArrival = new Date(laterArrivalIso)
  laterArrival.setHours(0, 0, 0, 0)
  return getHolidayDetails().some(
    (holiday) =>
      isHolidayObservedIn(holiday, country) &&
      earlierArrival <= holiday.holidayDate &&
      laterArrival > holiday.holidayDate
  )
}

function isHolidayObservedIn(
  { countries }: Holiday,
  country: string | undefined
): boolean {
  return !countries || countries.includes(country ?? '')
}

function getHolidayDetails(): Holiday[] {
  const today = new Date()
  const holidays: Holiday[] = [
    {
      id: 'valentines_day',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_valentines_day',
      cartLineTextKey: 'holidays.get_it_by_valentines_day',
      holidayDate: new Date(today.getFullYear(), 1, 13, 0, 0, 0, 0), // February 13th (day before Valentine's Day)
    },
    {
      id: 'mothers_day_uk',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_mothers_day',
      cartLineTextKey: 'holidays.get_it_by_mothers_day',
      holidayDate: getUKMothersDayDate(today.getFullYear()),
      countries: ['GB'],
    },
    {
      id: 'mothers_day',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_mothers_day',
      cartLineTextKey: 'holidays.get_it_by_mothers_day',
      holidayDate: getUSMothersDayDate(today.getFullYear()),
    },
    {
      id: 'fathers_day',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_fathers_day',
      cartLineTextKey: 'holidays.get_it_by_fathers_day',
      holidayDate: getUSFathersDayDate(today.getFullYear()),
      countries: ['US', 'CA', 'GB'], // Father's Day is the 3rd Sunday of June in these countries; differs elsewhere (e.g. DE, ES)
    },
    {
      id: 'veterans_day',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_veterans_day',
      cartLineTextKey: 'holidays.get_it_by_veterans_day',
      holidayDate: new Date(today.getFullYear(), 10, 10, 0, 0, 0, 0), // November 10th (day before Veterans Day)
      countries: ['US'],
    },
    {
      id: 'christmas',
      arrivesBeforeHolidayTextKey: 'holidays.arrives_before_christmas',
      cartLineTextKey: 'holidays.get_it_by_christmas',
      holidayDate: new Date(today.getFullYear(), 11, 24, 0, 0, 0, 0), // 24th of December
    },
  ]
  return holidays.sort(
    (a, b) => a.holidayDate.getTime() - b.holidayDate.getTime()
  )
}

/**
 * US Mother's Day occurs on the second Sunday of May.
 * We return the date of the day before Mother's Day.
 */
function getUSMothersDayDate(year: number): Date {
  const mothersDay = new Date(year, 4, 1, 0, 0, 0, 0) // 1st of May
  const dayOfWeek = mothersDay.getDay()
  const daysToAdd = dayOfWeek === 0 ? 7 : 14 - dayOfWeek // Days to add to get to second Sunday
  mothersDay.setDate(mothersDay.getDate() + daysToAdd - 1) // A day before second Sunday
  return mothersDay
}

/**
 * US Father's Day occurs on the third Sunday of June.
 * We return the date of the day before Father's Day.
 */
function getUSFathersDayDate(year: number): Date {
  const fathersDay = new Date(year, 5, 1, 0, 0, 0, 0) // 1st of June
  const dayOfWeek = fathersDay.getDay()
  const daysToAdd = dayOfWeek === 0 ? 14 : 21 - dayOfWeek // Days to add to get to third Sunday
  fathersDay.setDate(fathersDay.getDate() + daysToAdd - 1) // A day before third Sunday
  return fathersDay
}

/**
 * UK Mother's Day (Mothering Sunday) occurs 3 weeks before Easter Sunday.
 * We return the date of the day before UK Mother's Day.
 */
function getUKMothersDayDate(year: number): Date {
  const easter = getEasterDate(year)
  const ukMothersDay = new Date(easter)
  ukMothersDay.setDate(easter.getDate() - 21) // 3 weeks before Easter
  ukMothersDay.setDate(ukMothersDay.getDate() - 1) // Day before UK Mother's Day
  return ukMothersDay
}

/**
 * Calculate Easter Sunday date using the Anonymous Gregorian algorithm.
 * https://en.wikipedia.org/wiki/Date_of_Easter#Anonymous_Gregorian_algorithm
 */
export function getEasterDate(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day, 0, 0, 0, 0)
}

interface ShouldEncourageParams {
  shippingOption: ShippingOptionWithArrival
  freeShippingOption: ShippingOptionWithArrival | undefined
  holidayDate: Date
}

function shouldEncourageExpressShippingForHoliday({
  shippingOption,
  freeShippingOption,
  holidayDate,
}: ShouldEncourageParams): boolean {
  try {
    const today = new Date()
    today.setHours(0, 0, 0, 0) // At midnight so comparison is easier

    if (!freeShippingOption) {
      return false
    }

    // This function should return true if the current option is the free shipping option, and it arrives before the holiday,
    // or all the following:
    // 1. The free shipping option doesn't arrive by the holiday
    // 2. The current shipping option arrives by the holiday
    const freeArrivalDate = new Date(freeShippingOption.promisedArrivalDate.iso)
    freeArrivalDate.setHours(0, 0, 0, 0) // At midnight so comparison is easier

    const currentArrivalDate = new Date(shippingOption.promisedArrivalDate.iso)
    currentArrivalDate.setHours(0, 0, 0, 0) // At midnight so comparison is easier

    if (shippingOption.shippingOptionName === SHIPPING_OPTION_FREE) {
      return currentArrivalDate <= holidayDate
    }
    return freeArrivalDate > holidayDate && currentArrivalDate <= holidayDate
  } catch (e) {
    logger.error('shouldShowArrivesBeforeHoliday error', e)
    return false
  }
}
